import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useHceSummaryApi, { HCE_SUMMARY_IN_PROGRESS_STATUSES } from 'hooks/useHceSummaryApi'
import useClipboard from 'hooks/useClipboard'
import { HceSummaryRating } from './HceSummaryRating'
import { formatDate } from 'config/i18n'
import { RANGE_PRESETS, DEFAULT_RANGE_PRESET, resolveRange, sameRange, msToArDay, presetForRange, formatRangePeriod } from './range'

// Errores devueltos por POST .../hce-summary/consumers/:id/runs (ver
// api/src/hce-summary/router.js) — 'not_found' cubre tanto flag apagado
// como "no puedo acceder a este consumer", a propósito, mismo criterio del
// backend de no distinguir el motivo.
const GENERATE_ERROR_LABEL = {
  daily_cap_reached: 'Se alcanzó el límite diario de resúmenes con IA para tu equipo — probá de nuevo más tarde.',
  cooldown: 'Ya se generó un resumen para este paciente hace poco — mostrando el más reciente.',
  enqueue_failed: 'No pudimos poner en marcha el resumen — probá de nuevo en un rato.',
  not_found: 'Esta función no está disponible para tu equipo.',
  // El backend valida el período (api/src/hce-summary/range.js) y estos dos
  // errores son alcanzables con dos clicks en los date pickers. Sin etiqueta
  // propia caían en el 'probá de nuevo' genérico de abajo, que le echa la culpa
  // al sistema por algo que el profesional puede corregir en dos segundos.
  invalid_range: 'La fecha "desde" es posterior a la "hasta" — revisá el período.',
  invalid_field: 'Alguna de las fechas del período no es válida.',
}

const STATUS_LABEL = {
  queued: 'En cola...',
  running: 'Generando el resumen...',
  discarded_no_records: 'Este paciente no tiene historia clínica registrada todavía.',
  // Distinto de 'sin historia' a propósito: el paciente puede tener años de
  // historia y nada en el período elegido, y la acción que necesita el
  // profesional es otra (ampliar el período, no cargar una evolución).
  discarded_no_records_in_range: 'No hay registros en el período elegido — probá con un período más amplio.',
  // No es un error: hay registros, pero con tan poco texto el resumen sería
  // "Sin datos" en las cinco secciones. Se dice qué falta y qué hacer, en vez
  // de caer en el 'No se pudo generar el resumen' genérico de más abajo.
  discarded_not_enough_content: 'La historia de este paciente todavía tiene muy poco texto para resumir — cargá alguna evolución y volvé a intentar.',
  discarded_flag_disabled: 'Esta función se deshabilitó para tu equipo.',
  discarded_daily_cap: 'Se alcanzó el límite diario de resúmenes para tu equipo.',
  discarded_cooldown: 'Ya se generó un resumen para este paciente hace poco.',
  failed_assembly: 'No pudimos armar la historia clínica de este paciente — probá de nuevo.',
  failed_deidentify: 'Ocurrió un error preparando la historia clínica — probá de nuevo.',
  failed_ai_call: 'La IA no pudo generar el resumen — probá de nuevo.',
  failed_save: 'El resumen se generó pero no se pudo guardar — probá de nuevo.',
  pipeline_crashed: 'Algo salió mal generando el resumen — probá de nuevo.',
}

// Cortes por tope de tokens (card 1023): reintentar con la misma historia
// falla igual, así que en vez del 'probá de nuevo' se sugiere acotar el período.
const TRUNCATED_ERROR_CODES = ['map_truncated_max_tokens', 'reduce_truncated_max_tokens']
const HISTORY_TOO_LONG_LABEL = 'El historial de este paciente es demasiado extenso para resumirlo de una sola vez. Probá acotando el período a resumir.'

const isTruncatedRun = run => TRUNCATED_ERROR_CODES.includes(run?.error?.code)

// El prompt (api/src/hce-summary/prompts.js) le pide al modelo un formato
// markdown fijo y simple: 5 títulos de sección con "## " y puntos con "- ".
// Bug real reportado por Juan Cruz en QA (2026-08-20): se mostraba el texto
// crudo, así que el profesional veía los "##"/"-" tal cual en vez de un
// resumen con títulos y viñetas. Deliberadamente NO se usa una librería de
// markdown ni dangerouslySetInnerHTML — el texto lo generó un LLM a partir
// de historia clínica de terceros, así que nunca debe interpretarse como
// HTML. Alcanza con estos dos patrones (los únicos que pide el prompt);
// cualquier otra línea se muestra como párrafo, igual que hoy.
const renderSummaryMarkdown = text => {
  if (!text) return null
  const lines = text.split('\n')
  const blocks = []
  let currentList = null

  const flushList = () => {
    if (currentList) blocks.push(currentList)
    currentList = null
  }

  lines.forEach((line, index) => {
    const heading = /^##\s+(.+)/.exec(line)
    const bullet = /^[-•]\s+(.+)/.exec(line)
    if (heading) {
      flushList()
      blocks.push(<div key={index} className="fw-bold text-decoration-underline mt-3 mb-1">{heading[1]}</div>)
    } else if (bullet) {
      if (!currentList) currentList = { key: index, items: [] }
      currentList.items.push(bullet[1])
    } else if (line.trim()) {
      flushList()
      blocks.push(<p key={index} className="mb-1">{line}</p>)
    }
  })
  flushList()

  return blocks.map(block =>
    Array.isArray(block?.items)
      ? <ul key={block.key} className="mb-1">{block.items.map((item, i) => <li key={i}>{item}</li>)}</ul>
      : block
  )
}

const SummaryText = ({ text, color, fontSize }) => (
  <div style={{ color, fontSize }}>{renderSummaryMarkdown(text)}</div>
)

// Bug real (QA-Cormos, card Resumen HCE con IA, 24/09): "Copiar resumen"
// copiaba el texto crudo — al pegarlo en otro campo se veía el markdown
// literal ("## Antecedentes...") en vez de negrita/subrayado, porque
// navigator.clipboard.writeText() solo escribe texto plano. Misma lectura
// de línea que renderSummaryMarkdown (los 2 únicos patrones que pide el
// prompt), pero armando un string de HTML en vez de elementos React —
// useClipboard.js lo manda como text/html además del texto plano de
// siempre. Mismo criterio de seguridad que la razón por la que
// renderSummaryMarkdown NO usa dangerouslySetInnerHTML: el texto lo generó
// un LLM a partir de historia clínica de terceros, nunca se interpreta
// como HTML sin escapar — solo LOS TAGS que arma esta función son HTML
// real, el contenido de cada línea siempre pasa por escapeHtml primero.
const escapeHtml = text => text
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const summaryToHtml = text => {
  if (!text) return ''
  const lines = text.split('\n')
  const parts = []
  let currentListItems = null

  const flushList = () => {
    if (currentListItems) parts.push(`<ul>${currentListItems.join('')}</ul>`)
    currentListItems = null
  }

  lines.forEach(line => {
    const heading = /^##\s+(.+)/.exec(line)
    const bullet = /^[-•]\s+(.+)/.exec(line)
    if (heading) {
      flushList()
      parts.push(`<div style="font-weight:bold;text-decoration:underline;margin-top:12px;margin-bottom:4px;">${escapeHtml(heading[1])}</div>`)
    } else if (bullet) {
      if (!currentListItems) currentListItems = []
      currentListItems.push(`<li>${escapeHtml(bullet[1])}</li>`)
    } else if (line.trim()) {
      flushList()
      parts.push(`<p style="margin-bottom:4px;">${escapeHtml(line)}</p>`)
    }
  })
  flushList()

  return parts.join('')
}

const HceSummary = ({ consumerID, enabled, onClose }) => {
  const { t } = useTranslation()
  const api = useHceSummaryApi({ consumerID, enabled })
  const { copied, copyToClipboard } = useClipboard()
  const run = api.run

  // Rango pedido (feedback 19/08). El default es "toda la historia", así que
  // para quien no toca nada el comportamiento es el de siempre.
  const [preset, setPreset] = useState(DEFAULT_RANGE_PRESET)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const selectedRange = resolveRange({ preset, customFrom, customTo })

  // El rango con el que se generó el resumen que está en pantalla, en días
  // argentinos, para poder compararlo con el elegido y para poder mostrarlo.
  const runRange = run ? { from: msToArDay(run.rangeFrom), to: msToArDay(run.rangeTo) } : null
  const runHadRange = !!run && (run.rangeFrom != null || run.rangeTo != null)
  // Un solo armador para los dos períodos que se muestran (el de la corrida
  // actual y el del resumen anterior) — estaban duplicados y ya habían divergido
  // en el formato de fecha.
  const periodText = range => formatRangePeriod(range, {
    formatDay: ms => formatDate(ms, 'dd-MMM-yyyy'),
    labels: {
      all: t('hce_summary_run_range_all_short', 'toda la historia'),
      openStart: t('hce_summary_range_open_start', 'inicio'),
      openEnd: t('hce_summary_range_open_end', 'en adelante'),
    },
  })


  // El selector arranca en el rango de la corrida que se está mostrando (ver
  // presetForRange). Se sincroniza UNA SOLA VEZ por corrida, y quien garantiza
  // eso es el guard por id — no la lista de deps.
  //
  // Esa distinción no es cosmética: la primera versión dependía de
  // `[run?.id, run?.rangeFrom, run?.rangeTo]` (primitivas, estables entre polls),
  // así que el efecto no se re-ejecutaba y el guard nunca corría. Un
  // sabotage-verify lo mostró: sacando el guard, la suite seguía verde — o sea
  // que la pieza que el comentario decía que protegía al usuario no estaba
  // probada por nada.
  //
  // Ahora la dep es `run` (objeto nuevo en cada vuelta del poll, cada 3s), y el
  // guard es lo que evita pisar lo que el profesional acaba de elegir. Es
  // testeable, y el test lo comprueba re-renderizando con la misma corrida en un
  // objeto nuevo. No hay riesgo del loop de setState de EHR.test.js porque el
  // guard corta ANTES de cualquier setState.
  const syncedRunRef = useRef(null)
  useEffect(() => {
    if (!run || syncedRunRef.current === run.id) return
    syncedRunRef.current = run.id
    const next = presetForRange({ from: msToArDay(run.rangeFrom), to: msToArDay(run.rangeTo) })
    setPreset(next.preset)
    setCustomFrom(next.customFrom)
    setCustomTo(next.customTo)
  }, [run]) // eslint-disable-line react-hooks/exhaustive-deps

  // Decisión 1C: avisar ANTES de gastar. Si el resumen que ya está a la vista
  // cubre otro período, generar con el rango elegido es una corrida nueva (el
  // cooldown es por rango, así que no lo va a frenar) — se dice, y decide el
  // profesional.
  const rangeDiffersFromRun = !!run && run.status === 'completed' && !sameRange(selectedRange, runRange)

  const handleGenerate = async () => {
    try {
      await api.generate(selectedRange)
    } catch {
      // el error ya queda en api.generateError, mostrado más abajo
    }
  }

  const handleRate = (rating, comment) => api.rate(run.id, rating, comment)

  const stillPolling = HCE_SUMMARY_IN_PROGRESS_STATUSES.includes(run?.status)

  // Comparación de strings YYYY-MM-DD: es válida y es la misma que hace el
  // backend con los milisegundos, sin necesidad de parsear nada acá.
  //
  // Se chequea del lado del cliente ADEMÁS del backend, no en su lugar: pedir
  // un rango invertido es un ida y vuelta al servidor para que te digan lo que
  // la pantalla ya sabía, y con el botón habilitado el profesional no tiene
  // ninguna pista de qué está mal hasta después de apretar.
  const invertedRange = preset === 'custom' && !!customFrom && !!customTo && customFrom > customTo
  const canGenerate = !api.generating && !api.inProgress && !invertedRange

  return (
    <div className="p-2 pe-0 font-nunito d-flex flex-column overflow-auto" style={{ width: '100%', maxHeight: '100%' }}>
      <div className="d-flex justify-content-between">
        <h4 className="mb-0" style={{ fontWeight: 700, color: '#13629B' }}>
          {t('hce_summary_title', 'Resumen de HCE con IA')}
        </h4>
        {onClose && (
          <button data-testid="hce-summary-close-button" type="button" className="btn-close float-end small" onClick={onClose} />
        )}
      </div>

      <p className="small mb-2">
        {t('hce_summary_description', 'Resume con IA toda la historia clínica de este paciente. La información se de-identifica antes de enviarse.')}
      </p>

      {/* Bug real (pedido de Nacho, 2026-08-12): sin este gate, mientras
          el primer GET .../runs/latest todavía está en vuelo `run` es
          `null` — el botón de abajo ya renderiza como "Generar resumen"
          (la etiqueta que usa cuando !run) y el bloque de resultado
          completo todavía no tiene nada que mostrar, así que un paciente
          que YA tiene un resumen generado parpadea unos segundos como si
          nunca hubiera generado ninguno, hasta que la respuesta llega y
          todo se re-renderiza de golpe. isLoading (a diferencia de
          isFetching) sólo es true en la carga inicial sin datos cacheados
          — un refetch en background con datos ya visibles no dispara este
          spinner, sigue mostrando el resumen actual sin parpadeo. */}
      {api.latestQuery.isLoading ? (
        <div className="d-flex align-items-center gap-2 text-muted small mb-2">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          {/* "Cargando..." no decía QUÉ se estaba cargando (feedback de la
              reunión del 19/08): al abrir el panel arranca sola una búsqueda de
              resúmenes ya generados, y el usuario no tenía forma de saberlo. */}
          {t('hce_summary_loading', 'Buscando resúmenes generados...')}
        </div>
      ) : (
        <>
          {api.generateError && (
            <div className="alert alert-warning py-2 small">
              {t(`hce_summary_generate_error_${api.generateError.response?.error}`,
                GENERATE_ERROR_LABEL[api.generateError.response?.error] || 'No pudimos generar el resumen — probá de nuevo.')}
            </div>
          )}

          {/* Pedido de Nacho (2026-08-11): visible antes del botón a propósito
              — es justo el dato que hace falta para decidir si generar de
              nuevo vale la pena, o si el resumen ya generado sigue vigente. */}
          {run && (run.finishedAt || run.createdAt) && (
            <p className="text-muted small mb-1">
              {t('hce_summary_last_generated', 'Último generado: {{date}}', {
                date: formatDate(run.finishedAt || run.createdAt, 'dd-MMM-yyyy HH:mm'),
              })}
            </p>
          )}

          {/* Decisión 2A: se muestra siempre la última corrida, con el rango que
              la generó escrito al lado. Sin esto, un resumen de "últimos 3 meses"
              se leería como si cubriera toda la historia. */}
          {run && (
            <p className="text-muted small mb-1" data-testid="hce-summary-run-range">
              {t('hce_summary_run_range', 'Período resumido: {{period}}', {
                period: periodText({ fromMs: run.rangeFrom, toMs: run.rangeTo }),
              })}
            </p>
          )}

          {/* Sólo un AVISO, nunca bloquea el botón — un profesional puede
              querer regenerar igual (ej. tras una mejora del motor) aunque
              la HCE no tenga entradas nuevas (pedido de Nacho, 2026-08-11).
              Cuenta real (no sólo sí/no) — pedido de seguimiento el mismo
              día ("cuántas entradas hay de nuevas"). */}
          {run?.newRecordsSinceLastRun === 0 && (
            <p className="text-muted small mb-2">
              {t('hce_summary_no_new_data', 'No hay cambios en la historia clínica desde este resumen.')}
            </p>
          )}
          {/* El contador del backend cuenta entradas nuevas en TODA la historia,
              sin mirar el período de la corrida (su fuente son las columnas
              created_at/updated_at, no la fecha clínica, que vive dentro del
              JSONB). Con un período elegido eso puede ser engañoso: un registro
              histórico cargado hoy cuenta como "nuevo", pero regenerar el mismo
              período de 3 meses no lo va a incluir y el resumen sale idéntico.
              Se dice, en vez de mandar al profesional a gastar una corrida para
              descubrirlo. */}
          {run?.newRecordsSinceLastRun > 0 && (
            <p className="text-muted small mb-2" data-testid="hce-summary-new-data">
              {runHadRange
                ? t('hce_summary_new_data_count_ranged', '{{count}} entrada(s) nueva(s) en la historia clínica desde este resumen — pueden no caer en el período resumido.', {
                  count: run.newRecordsSinceLastRun,
                })
                : t('hce_summary_new_data_count', '{{count}} entrada(s) nueva(s) en la historia clínica desde este resumen.', {
                  count: run.newRecordsSinceLastRun,
                })}
            </p>
          )}

          {/* Rango pedido (feedback 19/08, decisión 3C: presets + personalizado).
              Presets y no sólo desde/hasta libre porque cada par de fechas
              distinto es una pregunta distinta para el reuso: con rangos libres
              el resumen ya generado casi nunca se reusa y el costo se multiplica
              sin que nadie lo note. */}
          <div className="mb-2">
            <label className="form-label small mb-1" htmlFor="hce-summary-range">
              {t('hce_summary_range_label', 'Período a resumir')}
            </label>
            <select
              id="hce-summary-range"
              data-testid="hce-summary-range-select"
              className="form-select form-select-sm"
              value={preset}
              onChange={event => setPreset(event.target.value)}
            >
              {RANGE_PRESETS.map(option => (
                <option key={option.key} value={option.key}>
                  {t(`hce_summary_range_${option.key}`, option.label)}
                </option>
              ))}
            </select>

            {preset === 'custom' && (
              <div className="d-flex gap-2 mt-2">
                <div className="flex-fill">
                  <label className="form-label small mb-1" htmlFor="hce-summary-range-from">
                    {t('hce_summary_range_from', 'Desde')}
                  </label>
                  <input
                    id="hce-summary-range-from"
                    data-testid="hce-summary-range-from"
                    type="date"
                    className="form-control form-control-sm"
                    value={customFrom}
                    onChange={event => setCustomFrom(event.target.value)}
                    aria-invalid={invertedRange || undefined}
                    aria-describedby={invertedRange ? 'hce-summary-range-error' : undefined}
                  />
                </div>
                <div className="flex-fill">
                  <label className="form-label small mb-1" htmlFor="hce-summary-range-to">
                    {t('hce_summary_range_to', 'Hasta')}
                  </label>
                  <input
                    id="hce-summary-range-to"
                    data-testid="hce-summary-range-to"
                    type="date"
                    className="form-control form-control-sm"
                    value={customTo}
                    onChange={event => setCustomTo(event.target.value)}
                    aria-invalid={invertedRange || undefined}
                    aria-describedby={invertedRange ? 'hce-summary-range-error' : undefined}
                  />
                </div>
              </div>
            )}

            {invertedRange && (
              <p
                id="hce-summary-range-error"
                role="alert"
                className="small mt-1 mb-0"
                style={{ color: '#dc3545' }}
                data-testid="hce-summary-range-inverted"
              >
                {t('hce_summary_range_inverted', 'El "desde" tiene que ser anterior al "hasta".')}
              </p>
            )}
          </div>

          {/* Decisión 1C: se avisa, no se bloquea. El cooldown es por rango, así
              que este pedido NO va a ser rechazado — va a gastar una corrida de
              verdad, y eso tiene que estar dicho antes de apretar el botón. */}
          {rangeDiffersFromRun && (
            <div className="alert alert-warning py-2 small" data-testid="hce-summary-range-warning">
              {t('hce_summary_range_differs',
                'El resumen que estás viendo cubre otro período. Generar con el período elegido consume una corrida del equipo.')}
            </div>
          )}

          {/* Feedback de la reunión del 19/08: el resultado de la búsqueda va
              ANTES del botón — primero se informa qué se encontró, después se
              sugiere la acción. Estaba al revés (el botón arriba y este mensaje
              debajo), así que la primera cosa que se leía era "Generar resumen"
              sin saber todavía si ya había uno.

              Los otros dos mensajes de resultado ("Último generado" y las
              entradas nuevas) ya estaban arriba del botón por el mismo criterio,
              pedido en su momento por Nacho — este era el único caso
              inconsistente, justo el del paciente sin ningún resumen. */}
          {!run && !api.generating && (
            <p className="text-muted small mb-2">{t('hce_summary_empty', 'Todavía no se generó ningún resumen para este paciente.')}</p>
          )}

          <button
            type="button"
            data-testid="hce-summary-generate-button"
            className="btn btn-primary btn-sm align-self-start mb-2"
            disabled={!canGenerate}
            onClick={handleGenerate}
          >
            {api.generating
              ? t('hce_summary_starting_button', 'Iniciando...')
              : run
                ? t('hce_summary_regenerate_button', 'Generar de nuevo')
                : t('hce_summary_generate_button', 'Generar resumen')}
          </button>

          {run && api.inProgress && (
            <div className="d-flex align-items-center gap-2 text-muted small mb-2">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              {t(`hce_summary_status_${run.status}`, STATUS_LABEL[run.status] || 'Generando...')}
            </div>
          )}

          {run && stillPolling && api.gaveUp && (
            <div className="alert alert-secondary py-2 small">
              {t('hce_summary_gave_up', 'Está tardando más de lo esperado — sigue corriendo en segundo plano, esta pantalla se actualiza sola cuando termine.')}
            </div>
          )}

          {run && run.status === 'completed' && (
            <>
              {run.scope?.truncated && (
                <div className="alert alert-info py-2 small">
                  {t('hce_summary_truncated', 'Se resumió sólo la parte más reciente de la historia clínica por su tamaño ({{count}} de {{available}} registros).', {
                    count: run.scope.recordsCount, available: run.scope.recordsAvailable,
                  })}
                </div>
              )}
              <button
                type="button"
                data-testid="hce-summary-copy-button"
                className="btn btn-outline-secondary btn-sm align-self-start mb-2"
                onClick={() => copyToClipboard(run.summary, summaryToHtml(run.summary))}
              >
                {copied ? t('hce_summary_copied_button', 'Copiado') : t('hce_summary_copy_button', 'Copiar resumen')}
              </button>
              <SummaryText text={run.summary} color="#2c3e50" fontSize={16} />
              <HceSummaryRating rating={run.rating?.value ?? null} onRate={handleRate} />
            </>
          )}

          {run && !stillPolling && run.status !== 'completed' && (
            <div className="alert alert-secondary py-2 small" data-testid="hce-summary-status-alert">
              {isTruncatedRun(run)
                ? t('hce_summary_status_history_too_long', HISTORY_TOO_LONG_LABEL)
                : t(`hce_summary_status_${run.status}`, STATUS_LABEL[run.status] || 'No se pudo generar el resumen.')}
            </div>
          )}

          {/* El resumen que el profesional YA tenía, cuando el último intento no
              generó nada. Sin esto, elegir un período sin registros tapaba el
              resumen previo y no había forma de volver a verlo por 6hs: pedir de
              nuevo el período original choca con el cooldown, y ese rechazo
              refresca la pantalla al mismo descarte (ver getLatestCompletedRun en
              api/src/hce-summary/pg.js).

              Va DESPUÉS del aviso y con el período bien visible: es material de
              referencia, no el resultado de lo que se acaba de pedir, y
              confundir una cosa con la otra sería peor que no mostrarlo. */}
          {run && !stillPolling && run.status !== 'completed' && run.lastCompleted && (
            <div className="mt-2" data-testid="hce-summary-last-completed">
              <p className="text-muted small mb-1">
                {t('hce_summary_previous_summary', 'Resumen anterior ({{period}}), generado el {{date}}:', {
                  period: periodText({ fromMs: run.lastCompleted.rangeFrom, toMs: run.lastCompleted.rangeTo }),
                  date: run.lastCompleted.finishedAt ? formatDate(run.lastCompleted.finishedAt, 'dd-MMM-yyyy HH:mm') : '—',
                })}
              </p>
              <SummaryText text={run.lastCompleted.summary} color="#6c757d" fontSize={15} />
            </div>
          )}

        </>
      )}
    </div>
  )
}

export default HceSummary
export { HceSummary }
